import { Controller, FieldValues } from "react-hook-form";
import {
  fontWeightClass,
  getInputClassCustomized,
  getInputClassCustomizedTextArea,
} from "~/assets/styles/classes/InputClasses";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";

interface SIMPLInputWithControllerProps {
  name: string;
  control: any;
  rows?: number;
  rules?: Record<string, unknown>;
  id: string;
  label?: string;
  type?: string;
  fontWeight?: "bold" | "normal" | "extrabold";
  placeholder?: string;
  required?: boolean;
  errorLabel?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  background?: string;
  placeholderColor?: string;
  borderClass?:
    | "rounded-none"
    | "rounded-sm"
    | "rounded"
    | "rounded-md"
    | "rounded-lg"
    | "rounded-xl"
    | "rounded-2xl"
    | "rounded-3xl";
  onClick?: () => void;
  readOnly?: boolean;
  autoFocus?: boolean;
  testId?:string;
  messageRequired?: string;
}

export const SIMPLInput = ({
  name,
  control,
  rules,
  id,
  label,
  fontWeight = "normal",
  placeholderColor= "placeholder:text-red-500",
  type = "text",
  required = false,
  placeholder,
  errorLabel,
  disabled = false,
  hideLabel = false,
  borderClass = "rounded-lg",
  background = "white",
  readOnly = false,
  rows,
  onClick,
  autoFocus = false,
  testId,
  messageRequired
}: SIMPLInputWithControllerProps) => {
  const getInputElement = (field: FieldValues) => {
    if (type === "textarea") {
      return (
        <textarea
          {...field}
          rows={rows}
          onClick={onClick}
          autoFocus={autoFocus}
          className={`${getInputClassCustomizedTextArea(
            borderClass,
            errorLabel,
            background,
            readOnly,
            placeholderColor
          )} resize-y min-h-10 max-h-44`}
          id={id}
          readOnly={readOnly}
          placeholder={placeholder}
          disabled={disabled}
          style={{ fontFamily: "Arial, sans-serif" }}
           data-test-id={testId}
        />
      );
    }

    return (
      <input
        {...field}
        type={type}
        onClick={onClick}
        autoFocus={autoFocus}
        className={`${getInputClassCustomized(
          borderClass,
          errorLabel,
          background,
          readOnly
        )} ${errorLabel ? "placeholder:text-[#CA3532]" : ""}`}
        id={id}
        readOnly={readOnly}
        placeholder={placeholder}
        disabled={disabled}
        data-test-id={testId}
      />
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...rules,
        ...(required
          ? {
              required:messageRequired ?? `${label} is required`,
            }
          : {}),
      }}
      render={({ field, fieldState }) => {
        const showError = fieldState.error?.message || errorLabel;

        return (
          <div className="flex flex-col w-full">
            {/* Label */}
            {!hideLabel && (
              <label
                htmlFor={id}
                className={`inline-block cursor-pointer text-sm ${fontWeightClass(
                  fontWeight
                )} ${showError ? "text-error" : ""}`}
              >
                {label}
                {required && <span className="text-red-500"> *</span>}
              </label>
            )}

            {/* Input */}
            <div className="relative flex w-full">
              {getInputElement(field)}
            </div>

            {/* Error Message */}
            {showError && (
              <div className="mt-1 flex items-start gap-1 text-error max-w-full">
                <HighlightOffRoundedIcon
                  fontSize="small"
                  className="mt-[1px] shrink-0"
                />
                <span className="text-sm leading-[18px] break-words">
                  {showError}
                </span>
              </div>
            )}
          </div>
        );
      }}
    />
  );
};
